/* eslint-disable */
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import Cookies from "js-cookie";
import { toast } from "react-hot-toast";
import api from "../services/api";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const contentParam = urlParams.get('content');
    
    if (contentParam) {
      localStorage.setItem('content', contentParam);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!show2FA) return undefined;

    let interval = null;
    if (resendTimer > 0 && !canResend) {
      interval = setInterval(() => {
        setResendTimer((timer) => {
          if (timer <= 1) {
            setCanResend(true);
            return 0;
          }
          return timer - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [show2FA, resendTimer, canResend]);

  const is2FARequired = (data) => {
    const flag = data?.enable_2fa;
    return flag === true || flag === "true" || flag === 1;
  };

  const handleLoginSuccess = (response) => {
    // Intermediate 2FA response has no access_token — never redirect
    if (is2FARequired(response?.data) && !response?.data?.access_token) {
      return;
    }

    toast.success(response.message);
    if (response.data.pre_onboarded === true) {
      Cookies.set('sc_Token', response.data.access_token);
      localStorage.setItem('user_id', response.data.user_id);
      navigate("/user-details");
    } else {
      let userData = encodeURIComponent(JSON.stringify(response.data));
      const savedContent = localStorage.getItem('content');
      if (savedContent) {
        userData = encodeURIComponent(JSON.stringify({ ...response.data, route: `/dashboard/ai-agents/agent?content=${savedContent}` }));
        localStorage.removeItem('content');
      }

      window.open(`${process.env.REACT_APP_MAIN_URL}/login?user=${userData}`, '_self');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    try {
      const formdata = {
        email,
        password,
        check_2fa: true,
      }
      const response = await api.login(formdata);
      if (response.status === 'success') {
        if (is2FARequired(response?.data)) {
          if (response?.data?.user_id) {
            localStorage.setItem('user_id', response.data.user_id);
          }
          setShow2FA(true);
          setOtp(["", "", "", "", "", ""]);
          setCanResend(false);
          setResendTimer(60);
          toast.success(response.message || "Verification code sent to your email");
          return;
        }
        handleLoginSuccess(response);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error?.message || "Login failed");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (element, index) => {
    if (isNaN(element.value)) return false;

    setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);

    if (element.value && element.nextSibling) {
      element.nextSibling.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      e.preventDefault();

      if (otp[index]) {
        setOtp([...otp.map((d, idx) => (idx === index ? "" : d))]);
      } else if (index > 0) {
        const prevInput = e.target.previousElementSibling;
        if (prevInput) {
          prevInput.focus();
          setOtp([...otp.map((d, idx) => (idx === index - 1 ? "" : d))]);
        }
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.target.previousElementSibling?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      e.target.nextElementSibling?.focus();
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();

    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const response = await api.verifyLogin2FA({
        email,
        password,
        otp: otpValue,
      });

      if (response.status === 'success') {
        handleLoginSuccess(response);
      } else {
        toast.error(response.message || "Invalid verification code");
      }
    } catch (error) {
      toast.error(error?.message || "Verification failed");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResend2FA = async () => {
    setLoading(true);
    setCanResend(false);
    setResendTimer(60);

    try {
      const response = await api.login({
        email,
        password,
        check_2fa: true,
      });

      if (response.status === 'success') {
        toast.success(response.message || "Verification code resent to your email");
        setOtp(["", "", "", "", "", ""]);
      } else {
        toast.error(response.message || "Failed to resend code");
        setCanResend(true);
        setResendTimer(0);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to resend code");
      setCanResend(true);
      setResendTimer(0);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const allCookies = Cookies.get();
      for (const cookieName in allCookies) {
        Cookies.remove(cookieName);
      }
      const response = await api.googleLogin();
      window.location.href = response;
    } catch (error) {
      toast.error("Google login failed");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const otpComplete = otp.join("").length === 6;

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4 sm:p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <img 
            src="https://s3.us-east-2.amazonaws.com/sc-prod-embedded.salescaptain.com/resources/2025-03-18/full_logo.a46683a620fa53c3c554_1742299090688.png" 
            alt="SalesCaptain Logo" 
            className="h-10 object-contain"
          />
        </div>

        {show2FA ? (
          <>
            <h1 className="text-2xl font-semibold text-center mb-2">
              Verify your email
            </h1>
            <p className="text-gray-500 text-center mb-8">
              A verification code has been sent to{" "}
              <span className="font-bold text-blue-600">{email}</span>
            </p>

            <div className="mb-6 bg-blue-50 border border-blue-100 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Mail className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  Enter the 6-digit code sent to your email address to continue signing in.
                </span>
              </div>
            </div>

            <form onSubmit={handleVerify2FA} className="space-y-6">
              <div className="flex justify-center gap-2">
                {otp.map((data, index) => (
                  <input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={data}
                    className="w-12 h-12 border border-gray-200 rounded-lg text-center text-xl font-medium focus:border-blue-500 focus:outline-none"
                    onChange={(e) => handleOtpChange(e.target, index)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    onFocus={(e) => e.target.select()}
                    disabled={loading}
                  />
                ))}
              </div>

              {otpComplete && (
                <button
                  type="submit"
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : null}
                  Verify
                </button>
              )}

              <div className="text-center">
                {canResend ? (
                  <button
                    type="button"
                    onClick={handleResend2FA}
                    className="text-blue-600 hover:underline text-sm"
                    disabled={loading}
                  >
                    Didn't receive the code? Resend
                  </button>
                ) : (
                  <p className="text-gray-500 text-sm">
                    Resend code in {resendTimer} seconds
                  </p>
                )}
              </div>
            </form>

            <div className="mt-8 flex items-center justify-center text-sm">
              <button
                type="button"
                onClick={() => {
                  setShow2FA(false);
                  setOtp(["", "", "", "", "", ""]);
                  setCanResend(false);
                  setResendTimer(60);
                }}
                className="flex items-center text-gray-500 hover:text-blue-600"
                disabled={loading}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Sign In
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold text-center mb-2">
              Welcome back
            </h1>
            <p className="text-gray-500 text-center mb-8">
              Log in to your SalesCaptain account
            </p>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 w-full border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    required
                  />
                </div>
                
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12 w-full border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    required
                  />
                  <button 
                    type="button" 
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5"
                  >
                    {showPassword ? (
                      <Eye className="h-5 w-5" />
                    ) : (
                      <EyeOff className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-800">
                  Forgot password?
                </Link>
              </div>
              
              <button 
                type="submit" 
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : null}
                Sign In
              </button>
              
              <div className="relative flex items-center py-2">
                <div className="flex-grow border-t border-gray-200"></div>
                <span className="flex-shrink mx-4 text-gray-400 text-sm">or</span>
                <div className="flex-grow border-t border-gray-200"></div>
              </div>
              
              <button 
                type="button"
                className="w-full h-12 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2" 
                onClick={handleGoogleLogin}
                disabled={loading}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                  <path d="M1 1h22v22H1z" fill="none" />
                </svg>
                Continue with Google
              </button>
            </form>
            
            <div className="mt-8 text-center text-sm">
              <p className="text-gray-500">
                Don't have an account?{" "}
                <Link to="/signup" className="text-blue-600 hover:text-blue-800">
                  Sign Up
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
